import { useState } from "react";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { supabase } from "../supabaseClient";
import PortalFeedback from "./PortalFeedback";

export default function ChangePasswordPanel({ className = "" }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (password.length < 8) {
      setError("Use at least 8 characters for your new password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (updateError) {
      setError(updateError.message || "Could not update your password.");
      return;
    }

    setPassword("");
    setConfirmPassword("");
    setMessage("Password updated.");
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-[#EB040D]" />
        <h2 className="text-lg sm:text-xl font-bold tracking-tight">Password</h2>
      </div>
      <p className="mt-1 text-sm text-black/60">
        Change the password used for this portal account.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">New Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-md border border-black/10 bg-white px-3 py-2 pr-10 text-sm"
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-black/45 hover:text-black"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Confirm Password</label>
          <input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
            autoComplete="new-password"
            required
          />
        </div>

        {error ? <PortalFeedback type="error">{error}</PortalFeedback> : null}
        {message ? <PortalFeedback type="success">{message}</PortalFeedback> : null}

        <button
          type="submit"
          disabled={saving}
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-[#EB040D] px-4 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving..." : "Update Password"}
        </button>
      </form>
    </div>
  );
}
